import { InitiateUploadParams, MediaObjectUpload } from '@kudos/http-client'
import {
  MAX_FILE_SIZE_IN_BYTES,
  isFileSizeGreaterThanZero,
} from '@shared/uploads/ImageUploads.util'
import { MAX_ATTACHMENTS } from '@shared/wallPost/constants'
import FroalaEditorGlobal from 'froala-editor'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Control, FieldValues, useFieldArray, useFormContext } from 'react-hook-form'
import { ATTACHMENT_IMAGE_MIME_TYPES, ATTACHMENT_OTHER_MIME_TYPES } from '../AttachmentsField'

export type AttachmentUploadFn = (params: InitiateUploadParams) => Promise<MediaObjectUpload>

// Track if button is registered to avoid duplicate registration
let isAttachmentButtonRegistered = false

// Extend window type for Froala global callback
declare global {
  interface Window {
    FroalaEditor?: {
      DefineIcon: (
        name: string,
        options: { NAME?: string; SVG_KEY?: string; template?: string; PATH?: string },
      ) => void
      DefineIconTemplate: (name: string, template: string) => void
      RegisterCommand: (
        name: string,
        options: {
          title: string
          icon: string
          undo: boolean
          focus: boolean
          showOnMobile: boolean
          refreshAfterCallback: boolean
          callback: () => void
        },
      ) => void
    }
    __froalaAttachmentButtonCallback?: () => void
    __froalaAttachmentButtonDisabled?: boolean
  }
}

// Function to register the attachment button - call this whenever FroalaEditor is available
const registerAttachmentButtonCommand = () => {
  if (isAttachmentButtonRegistered) return

  try {
    // Register the custom attachment button command using insertFile icon
    FroalaEditorGlobal.RegisterCommand('insertAttachment', {
      title: 'Insert Attachment',
      icon: 'insertFile',
      undo: true,
      focus: true,
      showOnMobile: true,
      refreshAfterCallback: true,
      callback: function () {
        if (typeof window !== 'undefined' && window.__froalaAttachmentButtonCallback) {
          window.__froalaAttachmentButtonCallback()
        }
      },
      refresh: function ($btn: { toggleClass: (className: string, condition: boolean) => void }) {
        // Update button disabled state based on max attachments
        if (typeof window !== 'undefined' && window.__froalaAttachmentButtonDisabled) {
          $btn.toggleClass('fr-disabled', window.__froalaAttachmentButtonDisabled)
        }
      },
    })

    isAttachmentButtonRegistered = true
  } catch (_error) {
    // Registration failed, will retry later
  }
}

// Register the button immediately when module loads
registerAttachmentButtonCommand()

type UseFroalaAttachmentsProps = {
  mutationFn: (params: {
    originalFilename: string
    contentType: string
  }) => Promise<MediaObjectUpload>
  fieldName?: string
  control: Control<FieldValues>
}

export const useFroalaAttachments = (props?: UseFroalaAttachmentsProps) => {
  const {
    mutationFn: _mutationFn, // Not used directly - upload handled by AttachmentField components
    fieldName = 'attachments',
    control: _controlProp,
  } = props || {}

  // Always call hooks unconditionally
  const inputFileRef = useRef<HTMLInputElement>(null)
  const [maxSizeError, setMaxSizeError] = useState<boolean>(false)
  const [invalidTypeError, setInvalidTypeError] = useState<boolean>(false)
  const [emptyFileError, setEmptyFileError] = useState<boolean>(false)

  // Always use formContext.control to ensure we use the same control instance as AttachmentsField
  const formContext = useFormContext()
  const control = formContext.control

  const fieldArrayResult = useFieldArray({
    control,
    name: fieldName,
  })

  // Get the real append function from useFieldArray
  const { fields, append: realAppend, remove: realRemove } = fieldArrayResult
  const attachments = fields

  // Handle attachment button click - trigger file input
  const handleAddAttachmentClick = useCallback(() => {
    setMaxSizeError(false)
    setInvalidTypeError(false)
    setEmptyFileError(false)
    // mimics a click on the hidden file input
    inputFileRef?.current?.click()
  }, [])

  // Ensure button is registered (backup registration in component)
  useEffect(() => {
    registerAttachmentButtonCommand()

    // Also try after delays in case Froala loads asynchronously
    const timeout1 = setTimeout(registerAttachmentButtonCommand, 100)
    const timeout2 = setTimeout(registerAttachmentButtonCommand, 500)

    return () => {
      clearTimeout(timeout1)
      clearTimeout(timeout2)
    }
  }, [])

  // Store handleAddAttachmentClick function in global callback so it's accessible in button callback
  // Only set callback if props are provided (attachmentUploadFn is available)
  useEffect(() => {
    // Set global callback for Froala button only when props are provided
    if (typeof window !== 'undefined' && props) {
      window.__froalaAttachmentButtonCallback = handleAddAttachmentClick
    } else if (typeof window !== 'undefined') {
      window.__froalaAttachmentButtonCallback = undefined
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.__froalaAttachmentButtonCallback = undefined
      }
    }
  }, [handleAddAttachmentClick, props])

  // Handle file input change - same logic as AttachmentsField
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const selectedFile = e.target.files[0]
        // Validate file type
        const attachmentMimeTypes = [...ATTACHMENT_IMAGE_MIME_TYPES, ...ATTACHMENT_OTHER_MIME_TYPES]
        const isValidType = attachmentMimeTypes.includes(selectedFile.type)
        if (!isValidType) {
          setInvalidTypeError(true)
          e.target.value = ''
          return
        }
        if (!isFileSizeGreaterThanZero(selectedFile)) {
          setEmptyFileError(true)
          e.target.value = ''
          return
        }
        // Validate file size
        const isValidSize = selectedFile.size <= MAX_FILE_SIZE_IN_BYTES
        if (!isValidSize) {
          setMaxSizeError(true)
        } else {
          const attachmentData = {
            mediaObjectId: '',
            file: selectedFile,
            attachmentType: selectedFile.type.startsWith('image/') ? 'image' : 'document',
          }
          realAppend(attachmentData)
        }
        // Reset input so same file can be selected again
        e.target.value = ''
      }
    },
    [realAppend],
  )

  const isDisabled = attachments?.length >= MAX_ATTACHMENTS

  // Update global disabled state for button refresh callback
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__froalaAttachmentButtonDisabled = isDisabled
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.__froalaAttachmentButtonDisabled = undefined
      }
    }
  }, [isDisabled])

  return {
    inputFileRef,
    handleFileInputChange,
    maxSizeError,
    isDisabled,
    registerAttachmentButtonCommand,
    attachments,
    remove: realRemove,
    invalidTypeError,
    emptyFileError,
  }
}

export default useFroalaAttachments
